import React from 'react';
import {
  Container,
  Box,
  Tooltip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import useLoginCheck from 'src/hooks/useLoginCheck';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Divider, Typography } from '@mui/material';
import 'src/ui-component/ApplicationListTable/Table.css';
import { format } from 'date-fns';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { handleError } from 'src/redux-store/slices/errorSlice';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddNewPet from './AddNewPet';
import PetApplicationsDialog, {
  type PetApplicationsDialogPet
} from './PetApplicationsDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import { RootState } from 'src/redux-store/appStore';
import TablePagination from '@mui/material/TablePagination';
import { PetDetails } from 'src/types/PetPermit/petPermit.type';

// Custom styled component for table cells with different styles for header and body
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main || '#6E759F',
    color: theme.palette.common.white,
    fontSize: '0.9rem',
    padding: '8px',
    textTransform: 'capitalize'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '8px'
  }
}));

// Custom styled component for table rows
const StyledTableRow = styled(TableRow)(() => ({
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const StyledHeaderSortLabel = styled(TableSortLabel)(({ theme }) => ({
  color: `${theme.palette.common.white} !important`,
  '&:hover': {
    color: `${theme.palette.common.white} !important`
  },
  '&.Mui-active': {
    color: `${theme.palette.common.white} !important`
  },
  '& .MuiTableSortLabel-icon': {
    color: `${theme.palette.common.white} !important`
  }
}));

type PetTableSortKey = 'PetName' | 'PetType' | 'PetDOB' | 'PetBreed';
/** UI cycle matches prior Pet Name: default → asc → desc → default */
type SortCycle = 'default' | 'asc' | 'desc';

const PET_COLUMN_SORT_BY: Partial<Record<string, PetTableSortKey>> = {
  'Pet Name': 'PetName',
  'Pet Type': 'PetType',
  'Pet DOB': 'PetDOB',
  'Pet Breed': 'PetBreed'
};

// Main Component for displaying pet list
const MyPets: React.FC = () => {
  useLoginCheck();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = React.useState([]);
  /** Table / list refresh only — keeps breadcrumb, toolbar, search visible */
  const [tableLoading, setTableLoading] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);
  const [petDetails, setPetDetails] = React.useState<PetDetails>(null);
  const [totalDataCount, setTotalDataCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchText, setSearchText] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [petTableSort, setPetTableSort] = React.useState<{
    key: PetTableSortKey | null;
    dir: SortCycle;
  }>({ key: null, dir: 'default' });
  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );
  const columns = [
    'Pet Name',
    'Pet Type',
    'Pet DOB',
    'Pet Breed',
    'Pet Color',
    'Pet Sex',
    'Actions'
  ];

  const [returnToNewApplicationAfterPetSave, setReturnToNewApplicationAfterPetSave] =
    React.useState<string | null>(null);
  const [applicationsDialogOpen, setApplicationsDialogOpen] =
    React.useState<boolean>(false);
  const [selectedPetForApps, setSelectedPetForApps] =
    React.useState<PetApplicationsDialogPet | null>(null);

  const handleOpenPetApplications = (pet: {
    PetID: number;
    PetName?: string;
    PetType?: string;
  }): void => {
    setSelectedPetForApps({
      PetID: pet.PetID,
      PetName: pet.PetName,
      PetType: pet.PetType
    });
    setApplicationsDialogOpen(true);
  };

  const handleViewPetApplication = (applicationId: number): void => {
    setApplicationsDialogOpen(false);
    navigate(
      `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}/view-application/${applicationId}`
    );
  };

  React.useEffect(() => {
    const nav = location.state as {
      openAddPetFromNewApplication?: boolean;
      returnToNewApplication?: string;
    } | null;
    if (
      nav?.openAddPetFromNewApplication &&
      typeof nav.returnToNewApplication === 'string' &&
      nav.returnToNewApplication.length > 0
    ) {
      setReturnToNewApplicationAfterPetSave(nav.returnToNewApplication);
      setPetDetails(null);
      setOpen(true);
      navigate(`${location.pathname}${location.search || ''}`, {
        replace: true,
        state: {}
      });
    }
    // One-time state from New Application — replace so refresh does not reopen the dialog.
  }, [location.state, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  /**
   * Pet Type is Dog | Cat — header toggles Cats first ⇄ Dogs first only (same as sorting those two orders).
   * Other columns: default → asc → desc → default.
   */
  const togglePetColumnSort = (heading: string) => {
    const apiKey = PET_COLUMN_SORT_BY[heading];
    if (!apiKey) return;

    setPage(0);
    setPetTableSort((prev) => {
      if (prev.key !== apiKey) {
        return { key: apiKey, dir: 'asc' };
      }

      if (apiKey === 'PetType') {
        return {
          key: 'PetType',
          dir: prev.dir === 'asc' ? 'desc' : 'asc'
        };
      }

      if (prev.dir === 'default') return { key: apiKey, dir: 'asc' };
      if (prev.dir === 'asc') return { key: apiKey, dir: 'desc' };
      return { key: null, dir: 'default' };
    });
  };

  // Navigate to edit page with selected pet data
  const handleMoveToEdit = (data) => {
    navigate(
      `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}/pet-details/${data?.PetID}`,
      {
        state: {
          data: {
            permitId: currentPermitDetails?.id,
            page: page,
            rowsPerPage: rowsPerPage,
            petId: data?.PetID,
            PetDOB: data?.PetDOB,
            PetName: data?.PetName,
            PetType: data?.PetType,
            PetBreed: data?.PetBreed,
            PetColor: data?.PetColor,
            PetSex: data?.PetSex,
            PetHair: data?.PetHair,
            ChipNumber: data?.ChipNumber,
            IsSpayedNeutered: data?.IsSpayedNeutered
          }
        }
      }
    );
  };

  async function fetchListData (
    onDeletePet: (petId: number) => Promise<void>
  ): Promise<void> {
    setTableLoading(true);
    try {
      let url = `/pet/petDetails/all?Page=${page + 1}&Size=${rowsPerPage}`;
      const q = debouncedSearch.trim();
      if (q) {
        url += `&Search=${encodeURIComponent(q)}`;
      }
      if (petTableSort.key != null && petTableSort.dir !== 'default') {
        url += `&SortBy=${petTableSort.key}&SortDirection=${petTableSort.dir}`;
      }

      const data = await httpRequest('GET', url);

      if (data?.status === 'success') {
        const items = data?.data?.data ?? data?.data ?? [];
        setTotalDataCount(data?.data?.totalRecords ?? items?.length ?? 0);

        const formattedRows = [];
        const rowData = columns.map((item: string) => item.replace(/\s+/g, ''));
        items.forEach(
          (item: {
            PetDOB: string;
            PetName: string;
            PetType: string;
            PetBreed: string;
            PetColor: string;
            PetSex: string;
            PetID: number;
            ProfilePicture: string;
            applications: {
              ApplicationID: number;
              ApplicationStatus: string;
              PermitExpirationDate: string;
              LicenseStatus: 'Active' | 'Expired' | 'Expiring Soon' | 'No License'  ;
            }[];
          }) => {
            const rowObj = {};
            rowData.forEach((rowName: string) => {
              // Special handling for Status, Action, and LastActivityDate columns
              if (rowName === 'Action' || rowName === 'Actions') {
                rowObj[rowName] = (
                  <>
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        onClick={() => {
                          setPetDetails(item);
                          setOpen(true);
                        }}
                        color="primary"
                      >
                        <EditTwoToneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete" arrow>
                      <IconButton
                        onClick={() => onDeletePet(item?.PetID)}
                        color="error"
                        //Pet can be deleted if no application created till now
                        disabled={item?.applications?.length > 0}
                      >
                        <DeleteForeverIcon color="inherit" fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="View" arrow>
                      <IconButton
                        color="info"
                        onClick={() => handleMoveToEdit(item)}

                      >
                        <RemoveRedEyeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Application history" arrow>
                      <IconButton
                        color="secondary"
                        onClick={() => handleOpenPetApplications(item)}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                );
              } else if (rowName === 'PetDOB') {
                const petDob = item.PetDOB ? new Date(item.PetDOB) : null;
                rowObj[rowName] = petDob && !isNaN(petDob.getTime()) && petDob.getTime() !== 0
                  ? format(petDob, 'MM/dd/yyyy')
                  : '';
              } else {
                rowObj[rowName] = item[rowName] || '';
              }
            });
            formattedRows.push(rowObj);
          }
        );
        setRows(formattedRows);
        setPetDetails(null);
      } else {
        // Handle different types of errors
        if (data?.type === 'NETWORK_ERROR') {
          toast.error('Network error occurred');
        } else {
          toast.error(data?.message);
          dispatch(handleError(data?.message));
        }
      }
    } catch (error) {
      navigate(`/`);
      localStorage.setItem('petCount', '0');
    } finally {
      setTableLoading(false);
    }
  }

  async function deletePetData (PetID: number): Promise<void> {
    setTableLoading(true);
    try {
      const data = await httpRequest('DELETE', `/pet/petDetails/delete/${PetID}`);

      if (data?.status === 'success') {
        toast.success('Pet deleted successfully');
        await fetchListData(deletePetData);
      } else {
        if (data?.type === 'NETWORK_ERROR') {
          toast.error('Network error occurred');
        } else {
          toast.error(data?.message);
          dispatch(handleError(data?.message));
        }
      }
    } catch (error) {
      toast.error('An error occurred');
      dispatch(handleError('An error occurred'));
    } finally {
      setTableLoading(false);
    }
  }

  // Handle change in rows per page
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  // Fetch data when visiting page, modal closes, search, sort, or pagination changes
  React.useEffect(() => {
    if (open === false) fetchListData(deletePetData);
  }, [
    location.pathname,
    open,
    page,
    rowsPerPage,
    debouncedSearch,
    petTableSort.key,
    petTableSort.dir
  ]);

  return (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Pet License'} PageTitle={'My Pets'} />

      <Box mt={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            width: '100%',
            flexWrap: { sm: 'nowrap' },
            overflow: 'visible',
            pt: { sm: 0.5 }
          }}
        >
          <Button
            onClick={() => setOpen(true)}
            startIcon={<AddCircleOutlineIcon />}
            variant="contained"
            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            Add New Pet
          </Button>
          <TextField
            size="medium"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 260 },
              minWidth: { sm: 200 },
              maxWidth: { sm: 360 },
              marginLeft: { xs: 0, sm: 'auto' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Table section — loading overlay only here */}
        <Box
          mt={2}
          sx={{
            width: '100%',
            overflowX: 'auto',
            position: 'relative',
            minHeight: 200
          }}
        >
          {tableLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.65)',
                zIndex: 3,
                pointerEvents: 'none'
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <TableContainer component={Paper} className="table_container_list">
            <Table
              aria-label="customized table"
              sx={{ position: 'relative', opacity: tableLoading ? 0.55 : 1 }}
            >
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow>
                  {columns.map((heading) => {
                    const sortKey = PET_COLUMN_SORT_BY[heading];

                    return (
                      <StyledTableCell
                        key={heading}
                        align={heading !== 'Picture' ? 'center' : 'left'}
                      >
                        {sortKey ? (
                          <StyledHeaderSortLabel
                            active={
                              petTableSort.key === sortKey &&
                              petTableSort.dir !== 'default'
                            }
                            direction={
                              petTableSort.dir === 'desc' ? 'desc' : 'asc'
                            }
                            onClick={() => togglePetColumnSort(heading)}
                          >
                            {heading}
                          </StyledHeaderSortLabel>
                        ) : (
                          heading
                        )}
                      </StyledTableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              {/* Table Body */}
              <TableBody>
                {rows?.length > 0 ? (
                  rows.map((row, index) => (
                    <StyledTableRow key={index}>
                      {Object.keys(row).map((key) => (
                        <React.Fragment key={key}>
                          <StyledTableCell
                            align="center"
                            sx={{ maxWidth: '30rem' }}
                          >
                            {row[key]}
                          </StyledTableCell>
                        </React.Fragment>
                      ))}
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={columns.length} align="center">
                      <Typography variant="h6">
                        {tableLoading ? 'Loading…' : 'No data found'}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>

            <Divider sx={{ marginTop: 1 }} />
          </TableContainer>

           {/* Pagination controls */}
          <TablePagination
            sx={{ position: 'sticky', bottom: 0 }}
            className="pagination_parent_wrapper"
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalDataCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            disabled={tableLoading}
          />
        </Box>
      </Box>

      {/* Modal for Add/Edit Pet */}
      <AddNewPet
        setOpen={setOpen}
        open={open}
        petDetails={petDetails}
        returnAfterSavePath={returnToNewApplicationAfterPetSave}
        onClearReturnAfterSave={() => setReturnToNewApplicationAfterPetSave(null)}
      />

      <PetApplicationsDialog
        open={applicationsDialogOpen}
        pet={selectedPetForApps}
        onClose={() => {
          setApplicationsDialogOpen(false);
          setSelectedPetForApps(null);
        }}
        onViewApplication={handleViewPetApplication}
      />
    </Container>
  );
};

export default MyPets;
